import { useLazyQuery, useMutation } from "@netfront/apollo-providers";
import { Button, Dialog, Input, type DialogProps } from "@netfront/ui-library";
import type { Queries as HowlerQueries, NotificationSendMailsArgs, Queries, TemplateQueryGetArgs } from '@netfront/api-howler-types';
import { gql } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import { CONSTANTS } from "@/constants";
import { Form, Formik } from "formik";
import type { SmartCodeGroupGraphType, UserGraphType, UserQuery } from "@netfront/api-gelada-private-types";
import * as yup from 'yup';
import { useToast } from "@netfront/ekardo-content-library";
import DOMPurify from 'dompurify';

export interface InviteTeacherDialogProps extends Pick<DialogProps, 'isOpen' | 'onClose'> {}

type InviteTeacherFormValues = {
  name: string;
  email: string
};

const initialValues: InviteTeacherFormValues = {
  name: 'teacher',
  email: ''
};

const EmailTemplatePreview = ({ template, name, submissionTeacherName, groupCode }: {
  template?: string;
  name: string;
  submissionTeacherName: string;
  groupCode: string;
}) => {
  const renderedTemplate = useMemo(() => {
    if (!template) return '';

    return DOMPurify.sanitize(
      template
        .replaceAll('[SubmissionTeacherName]', submissionTeacherName.trim())
        .replaceAll('[GroupCode]', groupCode.trim())
        .replaceAll('[TeacherName]', name.trim())
    );
  }, [template, name, submissionTeacherName, groupCode]);

  if (!template) return null;

  return (
    <div
      dangerouslySetInnerHTML={{ __html: renderedTemplate }}
    />
  );
};

export const InviteTeacherDialog = ({ isOpen, onClose }: InviteTeacherDialogProps) => {
  const { EMAIL_TEMEPLATES } = CONSTANTS;

  const { handleToastSuccess } = useToast();

  const [user, setUser] = useState<UserGraphType>();
  const [emailTemplate, setEmailTemplate] = useState<string>();
  const [teacherGroupCode, setTeacherGroupCode] = useState<string>();

  const inviteTeacherSchema = yup.object({
    name: yup.string().trim().required('Teacher name is required'),
    email: yup.string().trim().email('Enter a valid email').required('Teacher email is required'),
  });

  const { handleQuery: getLoggedUser } = useLazyQuery<{ user: Pick<UserQuery, 'getLoggedUser'> }>({
    apiName: 'gelada',
    query: gql`
      query {
        user {
          getLoggedUser {
            firstname
            lastname
            memberships {
              userTypeId
              group {
                smartCodes {
                  code
                  groupId
                  userTypeId
                }
              }
            }
          }
        }
      }
    `,
    onCompleted: ({ user: { getLoggedUser } }) => {
      if(!getLoggedUser) return;
      setUser(getLoggedUser);
    }
  });


  const { handleQuery: getEmailTemplate } = useLazyQuery<
    Pick<HowlerQueries, 'template'>,
    TemplateQueryGetArgs
  >({
    apiName: 'howler',
    query: gql`
      query getMessage($id: Int!) {
        template {
          get(id: $id) {
            id
            content
            bodyContent
            layout {
              id
            }
            name
            smsUsage
            subject
            type
            usage
          }
        }
      }
    `,
    onCompleted: ({ template }) => {
      const returnedTemplate = template?.get;

      if (!returnedTemplate?.bodyContent) return;

      const membership = user?.memberships?.[0];

      const groupCode = membership?.group?.smartCodes
        ?.filter((code): code is SmartCodeGroupGraphType => Boolean(code))
        .find(({ userTypeId }) => userTypeId === membership.userTypeId)
        ?.code ?? '';

      setTeacherGroupCode(groupCode);
      setEmailTemplate(returnedTemplate.bodyContent);
    },
  });

  const { handleMutation: sendEmail, isLoading: isInviteSending } = useMutation<Pick<Queries, 'notification'>, NotificationSendMailsArgs>({
    apiName: 'howler',
    mutation: gql`
      mutation sendMails($notificationGroup: InputNotificationGroup!) {
        notification {
          sendMails(notificationGroup: $notificationGroup) {
            id
          }
        }
      }
    `,
  });

  useEffect(() => {
    if(!user) {
      getLoggedUser({});
      return;
    }

    if (!isOpen || emailTemplate) return;

    if(user) {
      getEmailTemplate({
        id: EMAIL_TEMEPLATES.INVITE_TEACHER.ID,
      });
    }
  }, [user, isOpen, emailTemplate, getEmailTemplate]);


  return (
    <Dialog
      additionalClassNames="px-8"
      isOpen={isOpen}
      title="Invite another teacher"
      onClose={onClose}
    >
      <p className="text-left mb-8 mt-4 text-sm">
        Send an invitation via email to another teacher at your school to register on this website. You may need to provide the access code located in the Information Pack for them to complete registration. Please do invite teachers from outside of your school.
      </p>
      <Formik<InviteTeacherFormValues>
        initialValues={initialValues}
        enableReinitialize={false}
        validationSchema={inviteTeacherSchema}
        onSubmit={async ({ email, name }) => {
          await sendEmail({
            notificationGroup: {
              notifications: [
                { 
                  receiver: email,
                  placeholders: [
                    { key: '[TeacherName]', value: name.trim() },
                    { key: '[SubmissionTeacherName]', value: `${user?.firstname?.trim()} ${user?.lastname?.trim()}`.trim() },
                    { key: '[GroupCode]', value: teacherGroupCode?.trim() },
                  ]
                },
              ],
              projectId: String(process.env.REACT_APP_PROJECT_ID),
              subject: 'Invitation to join the Parents and Teens portal',
              templateId: EMAIL_TEMEPLATES.INVITE_TEACHER.ID,
            },
          }).then(() => {
            handleToastSuccess({
              message: 'Invite has been sent!'
            });

            if(onClose) {
              onClose()
            }
          })
        }}
      >
        {({ values, handleChange, handleBlur }) => (
          <Form>
            <Input
              labelText="Teacher name"
              id="name"
              name="name"
              type="text"
              placeholder="Enter teacher name"
              onChange={handleChange}
              onBlur={handleBlur}
            />

            <Input
              labelText="Teacher email"
              id="email"
              name="email"
              type="email"
              placeholder="Enter teacher email"
              onChange={handleChange}
              onBlur={handleBlur}
            />

            <EmailTemplatePreview
              template={emailTemplate}
              name={values.name}
              submissionTeacherName={`${user?.firstname ?? ''} ${user?.lastname ?? ''}`}
              groupCode={teacherGroupCode ?? ''}
            />

            <Button
              text="Send invite"
              type="submit"
              isLoading={isInviteSending}
              isDisabled={isInviteSending || !user || !teacherGroupCode}
            />

          </Form>
        )}
      </Formik>
    </Dialog>
  );
};